import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Download,
  Upload,
  Loader2,
  Database,
  FileArchive,
  ShieldAlert,
  RefreshCw,
  Mail,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const formatBytes = (bytes) => {
  if (bytes === 0 || bytes == null) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const BackupRestore = () => {
  usePageTitle('Backup & Restore');
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreText, setRestoreText] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchInfo = async () => {
    setLoadingInfo(true);
    try {
      const res = await axios.get(`${API_URL}/admin/backup/info`);
      setInfo(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load backup info');
    } finally {
      setLoadingInfo(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/backup`, {
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `empmedic-backup-${Date.now()}.zip`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error('Choose a backup .zip file first');
      return;
    }
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      formData.append('confirm', 'RESTORE');

      const res = await axios.post(`${API_URL}/admin/backup/restore`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message || 'Backup restored');
      setRestoreFile(null);
      setRestoreText('');
      setShowConfirm(false);
      // Reload after a short pause so the user sees the toast
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="backup-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin-settings')}
          data-testid="backup-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Backup &amp; Restore
          </h1>
          <p className="text-slate-500 mt-1">
            Download a full offline copy of every record and uploaded file. Restore from a backup if needed.
          </p>
        </div>
      </div>

      {/* Current data overview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Current Data
            </CardTitle>
            <CardDescription>What will be included in the next backup</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchInfo}
            disabled={loadingInfo}
            data-testid="backup-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loadingInfo ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loadingInfo ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : info ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Total documents</div>
                  <div className="text-2xl font-semibold text-slate-900">{info.total_documents}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Uploaded files</div>
                  <div className="text-2xl font-semibold text-slate-900">{info.upload_files}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Uploads size</div>
                  <div className="text-2xl font-semibold text-slate-900">{formatBytes(info.upload_bytes)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {Object.entries(info.collection_counts).map(([name, count]) => (
                  <div
                    key={name}
                    className="flex items-center justify-between rounded border border-slate-100 bg-white px-3 py-2"
                  >
                    <span className="text-slate-600 capitalize">{name.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Download backup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-emerald-600" />
            Download Full Backup
          </CardTitle>
          <CardDescription>
            Includes the entire database and every uploaded file in a single .zip archive. Save it somewhere safe (cloud drive, encrypted disk).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="backup-download-btn"
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing...
              </>
            ) : (
              <>
                <FileArchive className="w-4 h-4 mr-2" /> Download Backup (.zip)
              </>
            )}
          </Button>
          <p className="text-xs text-slate-500 mt-3">
            Tip: schedule a recurring reminder (weekly/monthly) to download a fresh backup.
          </p>
        </CardContent>
      </Card>

      {/* Auto weekly backup */}
      <Card className="border-emerald-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-600" />
            Automatic weekly backup
          </CardTitle>
          <CardDescription>
            Every <strong>Monday at 08:00 UTC</strong> a fresh backup zip is emailed to{' '}
            <strong>chris@empmedic.com</strong>. Use the button below to trigger a test send right now.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            disabled={sendingWeekly}
            onClick={async () => {
              setSendingWeekly(true);
              try {
                const res = await axios.post(`${API_URL}/admin/backup/send-weekly-now`);
                const r = res.data || {};
                if (r.status === 'sent') {
                  toast.success(`Sent! ${r.total_records} records, ${(r.size_bytes / (1024 * 1024)).toFixed(1)} MB`);
                } else if (r.status === 'skipped_no_api_key') {
                  toast.error('Resend API key not configured.');
                } else if (r.status === 'skipped_too_large') {
                  toast.error('Backup is too large to email — please download manually.');
                } else if (r.status === 'error') {
                  toast.error(`Failed: ${r.message}`);
                } else {
                  toast.message('Backup task ran (see logs).');
                }
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to trigger weekly backup');
              } finally {
                setSendingWeekly(false);
              }
            }}
            data-testid="backup-test-weekly-btn"
          >
            {sendingWeekly ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" /> Send weekly backup now (test)
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Restore */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" />
            Restore From Backup
          </CardTitle>
          <CardDescription className="text-red-600">
            Destructive action. This will <strong>replace ALL current data</strong> with the contents of the uploaded backup. Cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="restore-file">Backup file (.zip)</Label>
            <Input
              id="restore-file"
              type="file"
              accept=".zip,application/zip"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              data-testid="backup-restore-file-input"
            />
            {restoreFile && (
              <p className="text-xs text-slate-500 mt-1">
                Selected: {restoreFile.name} ({formatBytes(restoreFile.size)})
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="restore-confirm">
              Type <code className="px-1 bg-slate-100 rounded">RESTORE</code> to enable the button
            </Label>
            <Input
              id="restore-confirm"
              value={restoreText}
              onChange={(e) => setRestoreText(e.target.value)}
              placeholder="RESTORE"
              data-testid="backup-restore-confirm-input"
            />
          </div>
          <Button
            variant="destructive"
            disabled={!restoreFile || restoreText !== 'RESTORE' || restoring}
            onClick={() => setShowConfirm(true)}
            data-testid="backup-restore-btn"
          >
            {restoring ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" /> Restore From Selected File
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              All current data (users, classes, students, certificates, uploads) will be wiped and replaced
              with the contents of <strong>{restoreFile?.name}</strong>. You will be logged out and the app
              will restart. There is no undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              className="bg-red-600 hover:bg-red-700"
              data-testid="backup-restore-confirm-btn"
            >
              Yes, restore now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupRestore;
